/**
 * Approval Hero Component
 * Stats banner showing user's approval queue summary
 */
import React from 'react';
import { 
  Clock, 
  CheckCircle2, 
  XCircle,
  AlertTriangle,
  TrendingUp,
  Shield
} from 'lucide-react';

export default function ApprovalHero({ stats, userRole }) {
  const hasOverdue = stats?.overdue > 0;
  
  return (
    <div className="bg-gradient-to-r from-[#111111] to-[#0A0A0A] border-b border-[#1F1F1F]">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Role Badge */}
        <div className="flex items-center gap-3 mb-4">
          <span className="px-3 py-1 text-xs font-medium rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
            <Shield size={12} className="inline mr-1" />
            {userRole || 'Approver'} (Level {stats?.approvalLevel || 1})
          </span>
          {hasOverdue && (
            <span className="px-3 py-1 text-xs font-medium rounded-full bg-red-500/20 text-red-400 border border-red-500/30 animate-pulse">
              <AlertTriangle size={12} className="inline mr-1" />
              {stats.overdue} Overdue
            </span>
          )}
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Pending */}
          <div className={`rounded-xl p-4 ${hasOverdue ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-[#1A1A1A] border border-[#2F2F2F]'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${hasOverdue ? 'bg-amber-500/20' : 'bg-amber-500/10'}`}>
                <Clock size={20} className="text-amber-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">
                  {stats?.pending || 0}
                </div>
                <div className="text-xs text-[#6B6B6B]">
                  Pending Approval
                </div>
              </div>
            </div>
          </div>
          
          {/* Overdue */}
          <div className={`rounded-xl p-4 ${hasOverdue ? 'bg-red-500/10 border border-red-500/30' : 'bg-[#1A1A1A] border border-[#2F2F2F]'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${hasOverdue ? 'bg-red-500/20' : 'bg-red-500/10'}`}>
                <AlertTriangle size={20} className={hasOverdue ? 'text-red-400 animate-pulse' : 'text-red-400'} />
              </div>
              <div>
                <div className={`text-2xl font-bold ${hasOverdue ? 'text-red-400' : 'text-white'}`}>
                  {stats?.overdue || 0}
                </div>
                <div className="text-xs text-[#6B6B6B]">
                  Overdue Items
                </div>
              </div>
            </div>
          </div>
          
          {/* Approved This Month */}
          <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <CheckCircle2 size={20} className="text-emerald-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">
                  {stats?.approvedThisMonth || 0}
                </div>
                <div className="text-xs text-[#6B6B6B]">
                  Approved This Month
                </div>
              </div>
            </div>
          </div>
          
          {/* Rejected */}
          <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <XCircle size={20} className="text-purple-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">
                  {stats?.rejectedThisMonth || 0}
                </div>
                <div className="text-xs text-[#6B6B6B]">
                  Rejected This Month
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Average Approval Time */}
        {stats?.averageApprovalTimeHours && (
          <div className="mt-4 flex items-center gap-2 text-sm text-[#6B6B6B]">
            <TrendingUp size={14} className="text-emerald-400" />
            <span>Average approval time: <span className="text-white font-medium">{stats.averageApprovalTimeHours} hours</span></span>
          </div>
        )}
      </div>
    </div>
  );
}
